import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

// Define viewport sizes based on Tailwind's default breakpoints
export type Viewport = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface ViewportContextType {
  viewport: Viewport;
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const ViewportContext = createContext<ViewportContextType | undefined>(undefined);

interface ViewportProviderProps {
  children: ReactNode;
}

// Breakpoint values in pixels (matching Tailwind CSS defaults)
const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Provider component that tracks and provides the current viewport size.
 * Also applies appropriate CSS variables for responsive layouts.
 */
export const ViewportProvider = ({ children }: ViewportProviderProps) => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : breakpoints.lg);
  const [height, setHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);

  // Determine current viewport size based on width
  const getViewport = (width: number): Viewport => {
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  };

  const [viewport, setViewport] = useState<Viewport>(getViewport(width));

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = () => {
      // Set window width/height to state
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
      setViewport(getViewport(window.innerWidth));
      
      // Apply viewport height CSS variable
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      
      // Fix mobile Safari 100vh issue
      document.documentElement.style.setProperty('--real-viewport-height', `${window.innerHeight}px`);
    };
    
    // Initial setup
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away so state gets updated with initial window size
    handleResize();
    
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  // Helper booleans for common cases
  const isMobile = viewport === 'xs' || viewport === 'sm';
  const isTablet = viewport === 'md';
  const isDesktop = viewport === 'lg' || viewport === 'xl' || viewport === '2xl';

  return (
    <ViewportContext.Provider value={{ viewport, width, height, isMobile, isTablet, isDesktop }}>
      {children}
    </ViewportContext.Provider>
  );
};

/**
 * Custom hook to use the viewport context
 */
export const useViewport = () => {
  const context = useContext(ViewportContext);
  if (context === undefined) {
    throw new Error('useViewport must be used within a ViewportProvider');
  }
  return context;
};

/**
 * Viewport handler component that ensures proper viewport behavior
 * across different devices, especially on mobile.
 */
const ViewportHandler = () => {
  useEffect(() => {
    // Fix mobile viewport issues
    const setVhVariable = () => {
      // This ensures UI elements respect the actual visible height
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Run once on mount
    setVhVariable();

    // Update on resize and orientation change
    window.addEventListener('resize', setVhVariable);
    window.addEventListener('orientationchange', setVhVariable);

    return () => {
      window.removeEventListener('resize', setVhVariable);
      window.removeEventListener('orientationchange', setVhVariable);
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default ViewportHandler; 